import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, Search, AlertCircle, Loader2, Users, Eye, ChevronDown, Check
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const LEAD_STATUSES = ['Open', 'In Progress', 'Complete', 'Closed Won', 'Closed Lost'];

const STATUS_STYLES = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  'Complete': 'bg-cyan-100 text-cyan-700',
  'Closed Won': 'bg-green-100 text-green-700',
  'Closed Lost': 'bg-red-100 text-red-700',
};

const parseRequirements = (value) => (value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const RequirementMultiSelect = ({
  options,
  value,
  onChange,
  testId = 'input-requirements',
  placeholder = 'Type to search requirements...',
  emptyLabel = 'No requirements configured',
}) => {
  const containerRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const filtered = options.filter((option) => {
    const normalizedQuery = query.trim().toLowerCase();
    return !value.includes(option.name)
      && (!normalizedQuery
        || option.name?.toLowerCase().includes(normalizedQuery)
        || option.description?.toLowerCase().includes(normalizedQuery));
  });

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((requirement) => (
            <span key={requirement} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border border-orange-200 text-orange-700 rounded-sm text-sm">
              {requirement}
              <button type="button" onClick={() => onChange(value.filter((item) => item !== requirement))}
                aria-label={`Remove ${requirement}`} className="text-orange-400 hover:text-orange-700">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input type="text" value={query}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-500">
              {options.length === 0 ? emptyLabel : 'No matching entries'}
            </div>
          ) : filtered.map((option) => (
            <button key={option.id} type="button"
              onClick={() => {
                onChange([...value, option.name]);
                setQuery('');
                setOpen(false);
              }}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900">{option.name}</span>
                {option.description && <span className="block text-xs text-slate-500 truncate">{option.description}</span>}
              </span>
              <Check className="h-4 w-4 shrink-0 text-slate-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const initialFormData = { name: '', lead_source_id: '', email: '', phone: '', product: '', requirements: [], receivables: [], remarks: '' };

export const Leads = () => {
  const perms = detectPerms();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [phoneLookupState, setPhoneLookupState] = useState('idle');

  const fetchItems = useCallback(async () => {
    try { setLoading(true); const res = await axios.get(`${BACKEND_URL}/api/leads`, getAuthHeaders()); setItems(res.data); }
    catch (err) { setError('Failed to fetch leads'); } finally { setLoading(false); }
  }, []);

  const fetchLeadSources = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/lead-sources`, getAuthHeaders());
      setLeadSources(res.data);
    } catch {
      setError('Failed to fetch lead sources');
    }
  }, []);

  const fetchRequirements = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/requirements`, getAuthHeaders());
      setRequirements(res.data || []);
    } catch {
      setError('Failed to fetch requirements');
    }
  }, []);

  const fetchReceivables = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/receivables`, getAuthHeaders());
      setReceivables(res.data || []);
    } catch {
      setError('Failed to fetch receivables');
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([fetchItems(), fetchLeadSources(), fetchRequirements(), fetchReceivables()]);
    };
    initialize();
  }, [fetchItems, fetchLeadSources, fetchRequirements, fetchReceivables]);

  useEffect(() => {
    if (!showModal || editingItem) return undefined;
    const digits = (formData.phone || '').replace(/\D/g, '');
    if (digits.length !== 10) {
      setPhoneLookupState('idle');
      return undefined;
    }

    let cancelled = false;
    setPhoneLookupState('checking');
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/leads/lookup-by-phone`, {
          ...getAuthHeaders(),
          params: { phone: digits },
        });
        if (cancelled) return;
        if (response.data?.found) {
          setFormData(current => {
            if ((current.phone || '').replace(/\D/g, '') !== digits) return current;
            return {
              ...current,
              name: response.data.name || '',
              email: response.data.email || '',
            };
          });
          setPhoneLookupState('found');
        } else {
          setPhoneLookupState('not-found');
        }
      } catch {
        if (!cancelled) setPhoneLookupState('error');
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.phone, showModal, editingItem]);

  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };

  const openAddModal = () => { setFormData(initialFormData); setEditingItem(null); setPhoneLookupState('idle'); setError(''); setShowModal(true); };

  const openEditModal = (item) => {
    setFormData({
      name: item.name || '',
      lead_source_id: item.lead_source_id || '',
      email: item.email || '',
      phone: item.phone || '',
      product: item.product || '',
      requirements: parseRequirements(item.requirements),
      receivables: parseRequirements(item.receivables),
      remarks: item.remarks || '',
    });
    setEditingItem(item); setPhoneLookupState('idle'); setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (!formData.name.trim()) { setError('Name is required'); return; }
    if (!formData.lead_source_id) { setError('Lead source is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        requirements: formData.requirements.length ? formData.requirements.join(', ') : null,
        receivables: formData.receivables.length ? formData.receivables.join(', ') : null,
      };
      ['email', 'phone', 'product', 'remarks'].forEach(k => { if (!payload[k]?.trim()) payload[k] = null; });
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/leads/${editingItem.id}`, payload, getAuthHeaders());
        setShowModal(false);
        fetchItems();
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/leads`, payload, getAuthHeaders());
        setShowModal(false);
        navigate(`/admin/services/leads/${response.data.id}`);
      }
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save lead'); } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${BACKEND_URL}/api/leads/${id}`, getAuthHeaders()); setDeleteConfirm(null); fetchItems(); }
    catch (err) { setError('Failed to delete lead'); }
  };

  const filtered = items.filter(i => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = i.name?.toLowerCase().includes(q)
      || i.email?.toLowerCase().includes(q)
      || i.phone?.includes(searchQuery)
      || i.product?.toLowerCase().includes(q)
      || i.requirements?.toLowerCase().includes(q)
      || i.lead_source_name?.toLowerCase().includes(q);
    return matchesSearch && (statusFilter === 'All' || i.status === statusFilter);
  });

  const statusCounts = LEAD_STATUSES.reduce((acc, s) => { acc[s] = items.filter(i => i.status === s).length; return acc; }, {});

  return (
    <div data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Leads</h1>
          <p className="text-slate-600 mt-1">Manage your service leads</p>
        </div>
        {perms.canAdd && (
          <button onClick={openAddModal} data-testid="add-lead-btn"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
            <Plus className="h-5 w-5" /> Add Lead
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {LEAD_STATUSES.map(s => (
          <button key={s} onClick={() => setStatusFilter(statusFilter === s ? 'All' : s)}
            data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
            className={`bg-white rounded-sm border p-3 text-left transition-colors ${statusFilter === s ? 'border-orange-500 ring-1 ring-orange-500' : 'border-slate-200 hover:border-slate-300'}`}>
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-1 ${STATUS_STYLES[s]}`}>{s}</span>
            <p className="text-xl font-bold text-slate-900">{statusCounts[s] || 0}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by name, email, phone, product or lead source..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Users className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No leads found</h3>
            <p className="text-slate-500 mb-6">{items.length === 0 ? 'Start by adding your first lead' : 'Try adjusting your search or filter'}</p>
            {items.length === 0 && perms.canAdd && (
              <button onClick={openAddModal} className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600"><Plus className="h-5 w-5" /> Add First Lead</button>
            )}
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-10 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Phone</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Product</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Lead Source</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map(item => (
              <div key={item.id} data-testid={`lead-row-${item.id}`}
                className="grid grid-cols-1 md:grid-cols-10 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="col-span-2">
                  <span className="font-medium text-slate-900">{item.name}</span>
                  {item.email && <span className="block text-xs text-slate-500">{item.email}</span>}
                </div>
                <div className="text-slate-600 text-sm">{item.phone || '-'}</div>
                <div className="text-slate-600 text-sm">{item.product || '-'}</div>
                <div className="md:col-span-2 text-sm text-slate-600">{item.lead_source_name || '-'}</div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[item.status] || 'bg-slate-100 text-slate-700'}`}>{item.status}</span>
                </div>
                <div className="text-slate-500 text-xs">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => navigate(`/admin/services/leads/${item.id}`)} data-testid={`view-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"><Eye className="h-4 w-4" /></button>
                  {perms.canEdit && (
                  <button onClick={() => openEditModal(item)} data-testid={`edit-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"><Pencil className="h-4 w-4" /></button>
                  )}
                  {perms.canDelete && (
                  <button onClick={() => setDeleteConfirm(item.id)} data-testid={`delete-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this lead?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">{editingItem ? 'Edit Lead' : 'Add New Lead'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6"><AlertCircle className="h-5 w-5" /><span>{error}</span></div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <input type="text" name="phone" value={formData.phone}
                      onChange={(event) => setFormData(current => ({ ...current, phone: event.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      inputMode="numeric" maxLength={10} data-testid="input-phone"
                      className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                    {phoneLookupState === 'checking' && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-orange-500" />}
                    {phoneLookupState === 'found' && <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600" />}
                  </div>
                  {!editingItem && phoneLookupState === 'found' && <p className="text-xs text-green-600 mt-1">Existing lead found. Name and email have been filled automatically.</p>}
                  {!editingItem && phoneLookupState === 'not-found' && <p className="text-xs text-slate-500 mt-1">No existing lead found with this number.</p>}
                  {!editingItem && phoneLookupState === 'error' && <p className="text-xs text-amber-600 mt-1">Could not check this number. You can continue entering the lead manually.</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange} required data-testid="input-name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} data-testid="input-email"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lead Source <span className="text-red-500">*</span></label>
                  <select name="lead_source_id" value={formData.lead_source_id} onChange={handleInputChange} required data-testid="input-lead-source"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">Select lead source</option>
                    {leadSources.map(source => <option key={source.id} value={source.id}>{source.name}</option>)}
                  </select>
                  {leadSources.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Add a source from Service Hub → Lead Source before creating a lead.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Product</label>
                  <input type="text" name="product" value={formData.product} onChange={handleInputChange} data-testid="input-product"
                    placeholder="Enter product name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Requirements</label>
                  <RequirementMultiSelect options={requirements} value={formData.requirements}
                    onChange={(selected) => setFormData((current) => ({ ...current, requirements: selected }))} />
                  {requirements.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Add entries from Service Hub → Requirements before selecting them.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Receivables</label>
                  <RequirementMultiSelect options={receivables} value={formData.receivables}
                    onChange={(selected) => setFormData((current) => ({ ...current, receivables: selected }))}
                    testId="input-receivables" placeholder="Type to search receivables..."
                    emptyLabel="No receivables configured" />
                  {receivables.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Add entries from Service Hub → Receivables before selecting them.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Remarks</label>
                  <textarea name="remarks" value={formData.remarks} onChange={handleInputChange} rows={2} data-testid="input-remarks"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
